// S4.1b — THE FAILURE-CAUSE CATALOG, /quality/failure-causes (spec 07 §2.4,
// §4.1b; the glass half of fix #117).
//
// ═══════════════════════════════════════════════════════════════════════════
//  WHY THIS SCREEN EXISTS AT ALL
// ═══════════════════════════════════════════════════════════════════════════
// D-132 rules that a scrap resolution on a failure cause with no waste-cause
// mapping is REFUSED — loudly, naming the cause, never inventing a mapping that
// would misdirect the Pareto. It also rules (pt 5) that the pack re-seed is
// `ON CONFLICT DO NOTHING`, so every cause seeded before migration 0037 keeps
// its mapping NULL through every re-install. Both rulings are right, and
// together they left a qc on staging holding a 422 that told them to map a
// cause, with nowhere in the product to see WHICH causes were unmapped and
// nothing to map them with. The failure-cause catalog had no screen — the
// create route shipped glass-less, and the mapping route did not exist.
//
// So the screen's whole job is one sentence: an unmapped cause is VISIBLY
// unmapped, and (for a principal who may) mappable, in the same row.
//
// ═══════════════════════════════════════════════════════════════════════════
//  D-118, BOTH HALVES
// ═══════════════════════════════════════════════════════════════════════════
//  * A principal without `quality.catalog.manage` gets NO map control — not a
//    greyed one. The catalog is still fully READABLE by them (the list carries
//    no permission code at all, §5 row 7), including the unmapped chip: knowing
//    which causes will refuse a scrap is exactly the fact a floor lead needs
//    before they pick one.
//  * A control that is dead for a FIXABLE reason stays and says so: offline, a
//    module-05 catalog probe that failed, or a tenant whose waste catalog has
//    no active causes at all. Each of those is a different sentence, because
//    each has a different next step.
//
// THE OPTIONS COME FROM MODULE 05, THROUGH ITS OWN READ (doctrine 3):
// `GET /v1/waste/categories?include=causes&is_active=true` — the same door the
// floor capture grid and module 04's scrap-reason picker use, and the same one
// the server's refusal message now names. No new route was added for this
// screen; see `useWasteCauseOptions` for that argument.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { HelpTip } from "../../../../components/help-tip";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError, problemTitle } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useOnline } from "../../../floor-pwa/hooks/use-online";
import { isQualityReadRefused, useQualityPermissions } from "../hooks/permissions";
import {
  useMapFailureCauseWasteCause,
  useQualityFailureCauses,
  useWasteCauseOptions,
  type QualityFailureCause,
} from "../hooks/quality-queries";
import { localeText } from "../run/inspection-run";

/** `?mapping=unmapped` — the one filter this screen holds in the URL, because
 * "show me what will refuse a scrap" is the question somebody arrives with
 * from a 422 and wants to be able to share. */
export interface FailureCausesSearch {
  mapping?: string;
}

export function validateFailureCausesSearch(search: Record<string, unknown>): FailureCausesSearch {
  const value = search["mapping"];
  return typeof value === "string" && value === "unmapped" ? { mapping: "unmapped" } : {};
}

export function FailureCausesScreen() {
  const { t, i18n } = useTranslation();
  const perms = useQualityPermissions();
  const online = useOnline();

  const refused = isQualityReadRefused(perms, "quality.catalog.read");
  const canManage = perms.has("quality.catalog.manage");
  const causes = useQualityFailureCauses(!refused);
  // The option probe is issued ONLY for a principal who can actually map —
  // a read-only visitor has no use for it and no reason to spend the request.
  const options = useWasteCauseOptions(!refused && canManage);
  const mapMutation = useMapFailureCauseWasteCause();

  const [category, setCategory] = useState("");
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [editing, setEditing] = useState<QualityFailureCause | undefined>(undefined);

  const rows = useMemo(() => {
    const all = causes.data ?? [];
    return all
      .filter((row) => category === "" || row.category === category)
      .filter((row) => !onlyUnmapped || row.waste_cause_code === null);
  }, [causes.data, category, onlyUnmapped]);

  const unmappedCount = (causes.data ?? []).filter((row) => row.waste_cause_code === null).length;

  const columns: DataTableColumn<QualityFailureCause>[] = [
    { key: "code", header: t("quality.causes.colCode"), id: true },
    {
      key: "name",
      header: t("quality.causes.colName"),
      render: (row) => localeText(row.name_i18n, i18n.language, row.code),
    },
    {
      key: "category",
      header: t("quality.causes.colCategory"),
      render: (row) => t(`quality.causeCategory.${row.category}`),
    },
    {
      key: "mapping",
      header: t("quality.causes.colMapping"),
      render: (row) =>
        row.waste_cause_code === null ? (
          // The whole point of the screen, in one cell: an unmapped cause is
          // not blank and is not a dash — a dash reads as "nothing to say".
          <StatusChip variant="warn" data-testid="quality-cause-unmapped">
            {t("quality.causes.unmapped")}
          </StatusChip>
        ) : (
          <span data-testid="quality-cause-mapped">{row.waste_cause_code}</span>
        ),
    },
    {
      key: "actions",
      header: t("quality.causes.colActions"),
      actions: true,
      // D-118: no `quality.catalog.manage`, no action cell content. The row
      // stays readable — which is what a floor lead opened it for.
      render: (row) =>
        canManage ? (
          <Button
            size="sm"
            data-testid="quality-cause-map"
            onClick={() => {
              setEditing(row);
            }}
          >
            {row.waste_cause_code === null ? t("quality.causes.map") : t("quality.causes.remap")}
          </Button>
        ) : null,
    },
  ];

  return (
    <section className="quality-causes" data-testid="quality-causes">
      <PageHeader
        title={t("quality.causes.title")}
        actions={
          <>
            {/* WHAT AN UNMAPPED CAUSE COSTS, one press away (D-425). The
            unmapped-count banner below says it on screen when it applies. */}
            <HelpTip
              label={t("nav.helpAbout", { topic: t("quality.causes.title") })}
              data-testid="quality-causes-explainer"
            >
              {t("quality.causes.explainer")}
            </HelpTip>
          </>
        }
      />

      {!refused && unmappedCount > 0 ? (
        <p className="quality-banner" role="status" data-testid="quality-causes-unmapped-count">
          {t("quality.causes.unmappedCount", { count: unmappedCount })}
        </p>
      ) : null}

      <div className="filter-bar" data-testid="quality-causes-filters">
        <Field label={t("quality.causes.filterCategory")}>
          <Select
            value={category}
            data-testid="quality-causes-filter-category"
            onChange={(e) => {
              setCategory(e.target.value);
            }}
          >
            <option value="">{t("quality.causes.allCategories")}</option>
            {(["material", "machine", "method", "operator", "environment", "unknown"] as const).map(
              (value) => (
                <option key={value} value={value}>
                  {t(`quality.causeCategory.${value}`)}
                </option>
              ),
            )}
          </Select>
        </Field>
        <Field label={t("quality.causes.filterMapping")}>
          <Select
            value={onlyUnmapped ? "unmapped" : ""}
            data-testid="quality-causes-filter-mapping"
            onChange={(e) => {
              setOnlyUnmapped(e.target.value === "unmapped");
            }}
          >
            <option value="">{t("quality.causes.allMappings")}</option>
            <option value="unmapped">{t("quality.causes.onlyUnmapped")}</option>
          </Select>
        </Field>
      </div>

      {refused ? (
        <ForbiddenState title={t("quality.access.title")} body={t("quality.access.causes")} />
      ) : null}

      {!refused && causes.fetchStatus === "paused" && rows.length === 0 ? (
        <OfflineState title={t("quality.offline.title")} body={t("quality.offline.body")} />
      ) : null}

      {!refused && causes.isPending && causes.fetchStatus !== "paused" ? (
        <TableSkeleton rows={5} cols={columns.length} label={t("quality.common.loading")} />
      ) : null}

      {!refused && causes.error !== null && !(causes.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("quality.causes.error")}
          retryLabel={t("quality.common.retry")}
          requestIdLabel={t("quality.common.requestId")}
          requestId={requestIdOf(causes.error)}
          onRetry={() => {
            void causes.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !causes.isPending &&
      causes.fetchStatus !== "paused" &&
      causes.error === null &&
      rows.length === 0 ? (
        <div data-testid="quality-causes-empty">
          <EmptyState
            title={t("quality.causes.emptyTitle")}
            body={
              onlyUnmapped || category !== ""
                ? t("quality.causes.emptyFiltered")
                : t("quality.causes.emptyBody")
            }
          />
        </div>
      ) : null}

      {rows.length > 0 ? (
        <DataTable
          data-testid="quality-causes-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("quality.causes.tableCaption")}
          rowTestId="quality-cause-row"
        />
      ) : null}

      {editing !== undefined ? (
        <MapDialog
          cause={editing}
          online={online}
          options={options}
          mutation={mapMutation}
          onClose={() => {
            setEditing(undefined);
            mapMutation.reset();
          }}
        />
      ) : null}
    </section>
  );
}

interface MapDialogProps {
  cause: QualityFailureCause;
  online: boolean;
  options: ReturnType<typeof useWasteCauseOptions>;
  mutation: ReturnType<typeof useMapFailureCauseWasteCause>;
  onClose: () => void;
}

/**
 * The mapping control (§5 row 8a).
 *
 * A PICKER, never a free-text box: `waste_cause_code` is a value in another
 * module's catalog, and a field that let a qc type `MACHIN_DRIFT` would turn a
 * catalog decision into a spelling test whose only feedback is a 422. The
 * options come from module 05's own read and are pre-selected to the cause's
 * current mapping so a re-map opens on what it is changing.
 */
function MapDialog({ cause, online, options, mutation, onClose }: MapDialogProps) {
  const { t, i18n } = useTranslation();
  const [picked, setPicked] = useState(cause.waste_cause_code ?? "");

  const list = options.data ?? [];
  // A probe that was REFUSED is not the same as one that failed: the options
  // read carries no permission code, so a 403 here would mean the session is
  // gone, and that is the shell's business, not a sentence about waste causes.
  const probeFailed =
    options.error !== null &&
    !(options.error instanceof UnauthorizedError) &&
    !(options.error instanceof RequestError && options.error.httpStatus === 403);

  // D-118's fixable-reason half, in priority order: connectivity first (it
  // outranks everything and is the fastest to fix), then whether there is
  // anything to pick at all, then whether anything is picked.
  const blockedReason = !online
    ? t("quality.causes.dialogOffline")
    : options.isPending
      ? t("quality.causes.dialogLoadingOptions")
      : probeFailed
        ? t("quality.causes.dialogOptionsFailed")
        : list.length === 0
          ? t("quality.causes.dialogNoOptions")
          : picked === ""
            ? t("quality.causes.dialogPickRequired")
            : undefined;

  return (
    <Modal
      label={t("quality.causes.dialogTitle", { code: cause.code })}
      heading={t("quality.causes.dialogTitle", { code: cause.code })}
      closeLabel={t("quality.common.cancel")}
      className="quality-modal"
      testId="quality-cause-dialog"
      onClose={onClose}
    >
      <p className="quality-hint">{t("quality.causes.dialogBody")}</p>

      <Field label={t("quality.causes.dialogField")}>
        <Select
          value={picked}
          disabled={list.length === 0}
          data-testid="quality-cause-dialog-select"
          onChange={(e) => {
            setPicked(e.target.value);
          }}
        >
          <option value="">{t("quality.causes.dialogPickPlaceholder")}</option>
          {list.map((option) => (
            <option key={option.id} value={option.code}>
              {option.code} · {localeText(option.name_i18n, i18n.language, option.code)}
            </option>
          ))}
        </Select>
      </Field>

      <div className="quality-toolbar">
        <Button
          variant="primary"
          data-testid="quality-cause-dialog-save"
          disabled={blockedReason !== undefined}
          busy={mutation.isPending}
          busyLabel={t("quality.common.saving")}
          onClick={() => {
            mutation.mutate(
              { id: cause.id, wasteCauseCode: picked },
              {
                onSuccess: () => {
                  onClose();
                },
              },
            );
          }}
        >
          {t("quality.causes.dialogSave")}
        </Button>
      </div>

      {/* D-118 — the dead confirm says why, at the control. */}
      {blockedReason !== undefined ? (
        <p className="quality-hint" role="status" data-testid="quality-cause-dialog-blocked">
          {blockedReason}
        </p>
      ) : null}

      {/* The SERVER's own sentence on a refusal — the 422 names which codes
          resolve and where to list them, and re-minting that client-side would
          be a second, staler copy of module 05's rule. */}
      {mutation.isError ? (
        <p className="quality-error-note" role="alert" data-testid="quality-cause-dialog-failed">
          {problemTitle(
            mutation.error instanceof RequestError ? mutation.error.problem : undefined,
          ) ?? t("quality.causes.dialogFailed")}
        </p>
      ) : null}
    </Modal>
  );
}
